import { Controller } from 'react-hook-form';
import type { Control, UseFormClearErrors } from 'react-hook-form';

import Input from 'components/partials/input/input';
import Textarea from 'components/partials/textarea/textarea';
import Select from 'components/partials/select/select';

export interface ReportFormValues {
  title: string;
  description: string;
  powerBiId: string;
  productId: string;
}

export interface PowerBiReport {
  id: string;
  name?: string;
}

export interface ProductChoice {
  id: string | number;
  name?: string;
}

export interface PowerBiData {
  value?: string;
  label?: string;
}

interface ReportFormProps {
  control: Control<ReportFormValues>;
  disabled: boolean;
  powerBiReports: PowerBiReport[];
  clearErrors: UseFormClearErrors<ReportFormValues>;
  products: ProductChoice[];
  powerBiData: PowerBiData;
}

const ReportForm = ({
  control,
  disabled,
  powerBiReports,
  clearErrors,
  products,
  powerBiData,
}: ReportFormProps): React.JSX.Element => (
  <div className="flex h-full gap-8">
    <div className="flex flex-col gap-3 w-full">
      <Controller
        name="title"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            label="Report name"
            placeholder="Report name..."
            disabled={disabled}
            errorsNames={error}
            labelInTop={true}
            required={true}
            data-testid="report-name-input"
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => (
          <Textarea
            {...field}
            label="Description"
            placeholder="Please type report description..."
            disabled={disabled}
            errorsNames={error}
            required={true}
            data-testid="report-description-textarea"
          />
        )}
      />
    </div>

    <div className="flex flex-col gap-3 w-full">
      <Controller
        name="powerBiId"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => {
          const transformedOptions = powerBiReports.map((report) => ({
            value: report.id,
            label: report.name ?? '',
          }));

          transformedOptions.unshift({
            value: powerBiData.value ?? '',
            label: powerBiData.label ?? '',
          });

          return (
            <Select
              {...field}
              label="PowerBI title"
              disabled={disabled}
              options={transformedOptions}
              labelInTop={true}
              fieldError={
                error && { ...error, message: 'This field is required' }
              }
              required={true}
              onChange={(selectedOption) => {
                field.onChange(
                  (selectedOption as { value: string } | null)?.value
                );
              }}
              value={field.value}
              clearInputErr={() => clearErrors('powerBiId')}
              data-testid="report-powerbi-title-select"
            />
          );
        }}
      />

      <Controller
        name="productId"
        control={control}
        render={({ field }) => {
          const transformedOptions = products.map((product) => ({
            value: String(product.id),
            label: product.name ?? '',
          }));

          return (
            <Select
              {...field}
              label="Associated product"
              disabled={disabled}
              options={transformedOptions}
              onChange={(selectedOption) => {
                field.onChange(
                  (selectedOption as { value: string } | null)?.value || ''
                );
              }}
              value={field?.value || ''}
              labelInTop={true}
              isClearable={true}
              data-testid="report-associated-product-select"
            />
          );
        }}
      />
    </div>
  </div>
);

export default ReportForm;
